import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { COLORS } from '../../constants/theme';

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid ${COLORS.whiteSubtle};
  height: fit-content;
`;

const Section = styled.div`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${COLORS.whiteSubtle};
`;

const SectionTitle = styled.h4`
  color: ${COLORS.purple};
  margin-bottom: 1rem;
  font-size: 1.2rem;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ListItem = styled.li`
  margin-bottom: 0.5rem;
  color: rgba(255, 255, 255, 0.9);
`;

function PersonaCard({ persona }) {
  const { t } = useTranslation();

  const renderSection = (titleKey, items) => (
    <Section>
      <SectionTitle>{t(titleKey)}</SectionTitle>
      <List>
        {items.map(([label, value], i) => (
          <ListItem key={i}>
            <strong>{t(label)}:</strong> {Array.isArray(value) ? value.join(', ') : value}
          </ListItem>
        ))}
      </List>
    </Section>
  );

  return (
    <Card>
      <h3 style={{ color: COLORS.pink, marginBottom: '0.5rem', fontSize: '1.5rem' }}>{persona.name}</h3>
      <p className="follower-percentage">
        {t('follower_percentage')}: {persona.follower_base_is_estimated ? `~${persona.follower_base_percentage}% (est.)` : `${persona.follower_base_percentage}%`}
      </p>
      <p className="persona-description">{persona.description}</p>
      <p className="intro-narrative">{persona.intro_narrative}</p>

      {renderSection('demographics', [
        ['persona.fullName', persona.demographics.full_name],
        ['persona.jobTitle', persona.demographics.job_title],
        ['persona.ageRange', persona.demographics.age_range],
        ['persona.gender', persona.demographics.gender],
        ['persona.lifestyleFactors', persona.demographics.lifestyle_factors],
        ['persona.incomeLevel', persona.demographics.income_level],
        ['persona.educationLevel', persona.demographics.education_level]
      ])}

      {renderSection('personal_context', [
        ['persona.careerTrajectory', persona.personal_context.career_trajectory],
        ['persona.dailyResponsibilities', persona.personal_context.daily_responsibilities],
        ['persona.successMetrics', persona.personal_context.success_metrics],
        ['persona.toolsPlatforms', persona.personal_context.tools_platforms_used]
      ])}

      {renderSection('professional_background', [
        ['persona.industry', persona.professional_background.industry],
        ['persona.experience', persona.professional_background.experience],
        ['persona.skills', persona.professional_background.skills]
      ])}

      {renderSection('goals_challenges', [
        ['persona.primaryGoal', persona.goals_challenges.primary_goal],
        ['persona.secondaryObjective', persona.goals_challenges.secondary_objective],
        ['persona.keyChallenges', persona.goals_challenges.key_challenges],
        ['persona.urgencyLevel', persona.goals_challenges.urgency_level],
        ['persona.currentWorkarounds', persona.goals_challenges.current_workarounds]
      ])}

      {renderSection('decision_making_process', [
        ['persona.role', persona.decision_making_process.role],
        ['persona.evaluationCriteria', persona.decision_making_process.evaluation_criteria],
        ['persona.researchMethods', persona.decision_making_process.research_methods],
        ['persona.keyObjections', persona.decision_making_process.key_objections],
        ['persona.otherStakeholders', persona.decision_making_process.other_stakeholders]
      ])}

      {renderSection('communication_preferences', [
        ['persona.preferredChannels', persona.communication_preferences.preferred_channels],
        ['persona.contentTypes', persona.communication_preferences.content_types],
        ['persona.languageStyle', persona.communication_preferences.language_style],
        ['persona.eventsCommunities', persona.communication_preferences.events_communities]
      ])}

      {renderSection('value_proposition', [
        ['persona.howItHelps', persona.value_proposition.how_it_helps],
        ['persona.keyBenefits', persona.value_proposition.key_benefits],
        ['persona.valuableFeatures', persona.value_proposition.valuable_features],
        ['persona.messagingAngle', persona.value_proposition.messaging_angle]
      ])}

      {renderSection('engagement_strategy', [
        ['persona.bestChannels', persona.engagement_strategy.best_channels],
        ['persona.contentTopics', persona.engagement_strategy.content_topics],
        ['persona.callToAction', persona.engagement_strategy.call_to_action],
        ['persona.objectionsToPrep', persona.engagement_strategy.objections_to_prepare_for]
      ])}
    </Card>
  );
}

export default PersonaCard;
